
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Store, Zap } from 'lucide-react';
import { Button } from '../ui/Button';
import { PhoneMockup } from '../HeroAnimation';

interface HeroProps {
    onGetStarted: () => void;
    onWatchDemo: () => void;
}

export const Hero: React.FC<HeroProps> = ({ onGetStarted }) => {
    return (
        <section id="home" className="relative bg-white dark:bg-[#0A0B0F] pt-32 pb-32 overflow-hidden">
            <div className="container mx-auto px-6">
                <div className="flex flex-col lg:flex-row items-center gap-16">
                    <motion.div 
                        className="lg:w-1/2 text-center lg:text-left"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <div className="inline-block mb-6">
                            <span className="text-xs md:text-sm font-bold uppercase tracking-[2px] text-nobel-gold bg-nobel-gold/5 border border-nobel-gold/20 px-4 py-2 rounded-full">
                                SECURE. PRIVATE. CROSS-CHAIN.
                            </span>
                        </div>
                        
                        <h1 className="font-serif text-5xl md:text-6xl lg:text-[64px] text-[#0A0B0F] dark:text-white font-bold leading-[1.1] mb-6 tracking-tight">
                            The Sovereign <br className="hidden xl:block" />
                            <span className="text-stone-500 dark:text-stone-400">Financial Operating System</span>
                        </h1>
                        
                        <p className="text-lg md:text-[20px] text-gray-600 dark:text-gray-300 max-w-2xl mx-auto lg:mx-0 mb-10 leading-relaxed font-light">
                            Manage, transact, and settle digital assets with institutional-grade security. A unified platform for the privacy-focused economy.
                        </p>
                        
                        <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-4">
                            <Button onClick={onGetStarted} size="lg" variant="secondary" className="shadow-2xl shadow-nobel-gold/10 h-16 px-10 text-base font-bold rounded-xl group">
                                <Zap size={18} className="mr-3" /> Get Started <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform"/>
                            </Button>
                            <Button 
                                onClick={() => window.open('https://coffeeshop.ghostpay.fun', '_blank')} 
                                size="lg" 
                                variant="outline" 
                                className="bg-white dark:bg-transparent border-stone-200 dark:border-white/10 h-16 px-10 text-base font-bold hover:bg-stone-50 dark:hover:bg-white/5 rounded-xl text-[#0A0B0F] dark:text-white"
                            >
                                <Store size={18} className="mr-3"/> Explorer Demo
                            </Button>
                        </div>
                    </motion.div>
                    
                    <motion.div 
                        className="lg:w-1/2 flex justify-center mt-12 lg:mt-0 relative"
                        initial={{ opacity: 0, scale: 0.9, y: 30 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        {/* Background glow for hero image */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-radial-gradient from-nobel-gold/5 to-transparent blur-[80px] pointer-events-none"></div>
                        <div className="relative z-10">
                            <PhoneMockup />
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
